import React, { useEffect, useState } from "react";
import { childrenWithProps, classNames } from "../../tools/react.dom.helpers";
import { UrlTabsHelper } from "../../tools/urlTabsHelper";

// If syncWithUrl={true} – tabs are synchronized with the URL and respond to Back/Forward navigation.
// If not specified – tabs work locally without changing browser history.
// Example: <Tabs syncWithUrl={true}> ...

const Tabs = ({ children, controlled, value, onChange, className, type = Tabs.TEMPLATE.PRIMARY, syncWithUrl = false }) => {
    if (syncWithUrl) {
        UrlTabsHelper.init();
    }

    // Initial value: from props, or from URL if syncWithUrl, or from selected child
    let initialValue = value || (syncWithUrl && UrlTabsHelper.getTabFromUrl());

    if (!initialValue) { // If no initial value, find the first child with selected prop
        children.forEach((child) => {
            if (!initialValue && child.props.selected) {
                initialValue = child.props.value;
            }
        });
    }

    const [selectedTab, setSelectedTab] = useState(initialValue);

    useEffect(() => {
        // When selectedTab or syncWithUrl changes, update URL and call onChange
        if (selectedTab && syncWithUrl) {
            UrlTabsHelper.setTabInUrl(selectedTab);
        }
        onChange?.(selectedTab);

        // Subscribe to popstate only if syncWithUrl is enabled
        let unsubscribe = null;
        if (syncWithUrl) {
            unsubscribe = UrlTabsHelper.subscribe((tab) => {
                if (!controlled) setSelectedTab(tab);
                onChange?.(tab);
            });
        }

        // Cleanup subscription on unmount
        return unsubscribe || (() => {});
    }, []);

    // Handler for tab selection
    const selectTab = (tab) => {
        if (tab === selectedTab) return; // Do nothing if tab is already active
        setSelectedTab(tab);
        if (syncWithUrl) UrlTabsHelper.setTabInUrl(tab);
        onChange?.(tab);
    };

    return (
        <div className={classNames("nav-tabs__container", className, type && "nav-tabs__" + type)}>
            <div className="nav-tabs__scrollblock">
                <ul className="nav nav-tabs">
                    {childrenWithProps(children, (child) => ({
                        // Pass selected and onClick to each child tab
                        selected: controlled
                            ? value
                                ? value == child.props.value
                                : child.props.selected
                            : selectedTab == child.props.value,
                        onClick: () => selectTab(child.props.value),
                    }))}
                </ul>
            </div>
        </div>
    );
};

Tabs.TEMPLATE = {
    PRIMARY: "primary",
    SECONDARY: "secondary",
};

export default Tabs;
